import React, { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { queryClient } from '../main';

interface SocketContextType {
  socket: Socket | null;
  isConnected: boolean;
  isAuthenticated: boolean;
  joinRoom: (room: string) => void;
  leaveRoom: (room: string) => void;
  emit: (event: string, data?: any) => void;
  authenticate: (token: string, role: string) => void;
}

const SocketContext = createContext<SocketContextType | undefined>(undefined);

interface SocketProviderProps {
  children: ReactNode;
}

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showLogoutOption, setShowLogoutOption] = useState(false);

  // Check if user has auth data (was previously logged in)
  const hasAuthData = () => {
    try {
      const storedAuth = localStorage.getItem('auth-storage');
      if (storedAuth) {
        const authData = JSON.parse(storedAuth);
        return !!(authData.state?.token);
      }
    } catch {}
    return false;
  };

  useEffect(() => {
    // Initialize socket connection
    const socketInstance = io({
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      // Start with polling, then upgrade to websocket (more reliable behind proxies)
      transports: ['polling', 'websocket'],
      upgrade: true,
      withCredentials: true,
    });

    // Connection event handlers
    socketInstance.on('connect', () => {
      console.log('Socket connected:', socketInstance.id);
      setIsConnected(true);

      // Re-authenticate on reconnect if we have stored credentials
      const storedAuth = localStorage.getItem('auth-storage');
      if (storedAuth) {
        try {
          const authData = JSON.parse(storedAuth);
          if (authData.state?.token && authData.state?.user?.role) {
            socketInstance.emit('authenticate', {
              token: authData.state.token,
              role: authData.state.user.role
            });
          }
        } catch (e) {
          console.error('Failed to parse auth data for socket:', e);
        }
      }
    });

    // Authentication response
    socketInstance.on('connection-status', (status: string) => {
      if (status === 'authenticated') {
        console.log('Socket authenticated successfully');
        setIsAuthenticated(true);
      } else if (status === 'authentication-failed') {
        console.error('Socket authentication failed');
        setIsAuthenticated(false);
      }
    });

    socketInstance.on('disconnect', () => {
      console.log('Socket disconnected');
      setIsConnected(false);
    });

    socketInstance.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
      setIsConnected(false);
    });

    // Socket events that invalidate React Query cache
    // Order events
    socketInstance.on('order-created', () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      queryClient.invalidateQueries({ queryKey: ['kitchen-totals'] });
      queryClient.invalidateQueries({ queryKey: ['event'] });
    });

    socketInstance.on('order-updated', () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      queryClient.invalidateQueries({ queryKey: ['kitchen-totals'] });
    });

    socketInstance.on('kitchen-totals-updated', () => {
      queryClient.invalidateQueries({ queryKey: ['kitchen-totals'] });
    });

    // Event state changes
    socketInstance.on('event-started', () => {
      queryClient.invalidateQueries({ queryKey: ['event'] });
      queryClient.invalidateQueries({ queryKey: ['events'] });
    });

    socketInstance.on('event-updated', () => {
      queryClient.invalidateQueries({ queryKey: ['event'] });
      queryClient.invalidateQueries({ queryKey: ['events'] });
    });

    socketInstance.on('event-ended', () => {
      queryClient.invalidateQueries({ queryKey: ['event'] });
      queryClient.invalidateQueries({ queryKey: ['events'] });
    });

    socketInstance.on('break-changed', () => {
      queryClient.invalidateQueries({ queryKey: ['event'] });
      queryClient.invalidateQueries({ queryKey: ['breaks'] });
    });

    // Waiter events
    socketInstance.on('waiter-status-updated', () => {
      queryClient.invalidateQueries({ queryKey: ['waiters'] });
    });

    socketInstance.on('waiter-online', () => {
      queryClient.invalidateQueries({ queryKey: ['waiters'] });
    });

    // Dish events
    socketInstance.on('dishes-updated', () => {
      queryClient.invalidateQueries({ queryKey: ['dishes'] });
    });

    // Heartbeat
    const heartbeat = setInterval(() => {
      if (socketInstance.connected) {
        socketInstance.emit('ping');
      }
    }, 5000);

    // Refresh data when tab becomes visible (handles background tab issues)
    const handleVisibilityChange = () => {
      if (!document.hidden && socketInstance.connected) {
        queryClient.invalidateQueries();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    setSocket(socketInstance);

    // Cleanup
    return () => {
      clearInterval(heartbeat);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      // Remove all socket event listeners
      socketInstance.off('connect');
      socketInstance.off('disconnect');
      socketInstance.off('connect_error');
      socketInstance.off('connection-status');
      socketInstance.off('order-created');
      socketInstance.off('order-updated');
      socketInstance.off('kitchen-totals-updated');
      socketInstance.off('event-started');
      socketInstance.off('event-updated');
      socketInstance.off('event-ended');
      socketInstance.off('break-changed');
      socketInstance.off('waiter-status-updated');
      socketInstance.off('waiter-online');
      socketInstance.off('dishes-updated');
      socketInstance.disconnect();
    };
  }, []);

  // Show logout option after 20 seconds of being disconnected
  useEffect(() => {
    if (!isConnected && hasAuthData()) {
      const timer = setTimeout(() => {
        setShowLogoutOption(true);
      }, 20000);
      return () => clearTimeout(timer);
    } else {
      setShowLogoutOption(false);
    }
  }, [isConnected]);

  const handleLogout = () => {
    localStorage.removeItem('auth-storage');
    window.location.href = '/';
  };

  const joinRoom = (room: string) => {
    if (socket && isConnected) {
      socket.emit('join-room', room);
    }
  };

  const leaveRoom = (room: string) => {
    if (socket && isConnected) {
      socket.emit('leave-room', room);
    }
  };

  const emit = (event: string, data?: any) => {
    if (socket && isConnected) {
      socket.emit(event, data);
    }
  };

  const authenticate = useCallback((token: string, role: string) => {
    if (socket && isConnected) {
      socket.emit('authenticate', { token, role });
    }
  }, [socket, isConnected]);

  const value: SocketContextType = {
    socket,
    isConnected,
    isAuthenticated,
    joinRoom,
    leaveRoom,
    emit,
    authenticate,
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
      {/* Disconnect Overlay - shows when disconnected and user has auth data (was logged in) */}
      {!isConnected && hasAuthData() && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999] flex items-center justify-center">
          <div className="bg-gray-800 border border-gray-600 rounded-2xl p-8 max-w-md mx-4 text-center shadow-2xl">
            {/* Reconnecting indicator */}
            <div className="flex items-center justify-center mb-4 text-yellow-400">
              <svg className="animate-spin h-8 w-8 mr-3" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span className="text-xl font-medium">ממתין לחיבור...</span>
            </div>

            <p className="text-gray-400 text-sm">
              מתחבר מחדש לשרת
            </p>

            {showLogoutOption && (
              <button
                onClick={handleLogout}
                className="mt-6 text-gray-400 hover:text-white text-sm underline transition-colors"
              >
                התנתק וחזור למסך הכניסה
              </button>
            )}
          </div>
        </div>
      )}
    </SocketContext.Provider>
  );
};

export const useSocket = (): SocketContextType => {
  const context = useContext(SocketContext);
  if (context === undefined) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};